import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useRestaurant } from '../../context/RestaurantContext';
import { 
  Search, X, Plus, Minus, Trash2, ShoppingCart, CreditCard, Banknote,
  ParkingCircle, RotateCcw, Clock, User, AlertTriangle, Receipt,
  Maximize2, Minimize2, Grid3X3, List, ChevronRight, AlertCircle, WifiOff, Keyboard,
  Wifi, Server
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../components/ui/tooltip';
import { toast } from 'sonner';
import ProductGrid from './pos/ProductGrid';
import OrderPanel from './pos/OrderPanel';
import PaymentModal from './pos/PaymentModal';
import ParkedOrdersDrawer from './pos/ParkedOrdersDrawer';
import BillSplitter from './pos/BillSplitter';
import DuplicateOrderWarning from './pos/DuplicateOrderWarning';
import OfflineBanner, { OfflineIndicator } from '../../components/OfflineBanner';
import useOfflineMode from '../../hooks/useOfflineMode';
import localSync from '../../utils/localSync';

export default function POS() {
  const { t } = useTranslation();
  const { api } = useAuth();
  const { selectedRestaurant } = useRestaurant();
  
  // Offline Mode Hook
  const {
    isOnline,
    isServerReachable,
    isOffline,
    pendingCount,
    isSyncing,
    lastSyncTime,
    queueOrder,
    triggerSync,
    cacheMenu,
    getOfflineMenu
  } = useOfflineMode(api);
  
  // Menu & Categories
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  
  // Current Order
  const [currentOrder, setCurrentOrder] = useState({
    items: [],
    notes: '',
    customer_name: '',
    table_number: ''
  });
  
  // UI State
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // grid or list
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showParkedOrders, setShowParkedOrders] = useState(false);
  const [showBillSplitter, setShowBillSplitter] = useState(false);
  const [showDuplicateWarning, setShowDuplicateWarning] = useState(false);
  const [duplicateOrder, setDuplicateOrder] = useState(null);
  const [parkedOrders, setParkedOrders] = useState([]);
  const [parkedOrdersCount, setParkedOrdersCount] = useState(0);
  const [splitOrder, setSplitOrder] = useState(null);

  // Local Network Sync State (for offline fallback)
  const [localSyncStatus, setLocalSyncStatus] = useState({
    isHost: false,
    connected: false,
    peersCount: 0
  });

  // Initialize Local Sync for offline fallback
  useEffect(() => {
    if (!selectedRestaurant) return;

    // If this is the main POS (determined by localStorage flag), start as host
    const isMainPOS = localStorage.getItem('isPrimaryPOS') === 'true';
    
    if (isMainPOS && isOffline) {
      // Start as host when offline
      localSync.startAsHost(selectedRestaurant.id).then(() => {
        setLocalSyncStatus(localSync.getStatus());
        toast.info('Local sync active - Other devices can connect');
      }).catch(console.error);
    }

    // Listen for local sync events
    const handleSyncStatus = () => setLocalSyncStatus(localSync.getStatus());
    const handleNewOrder = (order) => {
      toast.info(`Order from ${order.source || 'local device'}`, {
        description: `Table ${order.table_number || 'N/A'}`
      });
    };

    localSync.on('connected', handleSyncStatus);
    localSync.on('disconnected', handleSyncStatus);
    localSync.on('order_created', handleNewOrder);

    return () => {
      localSync.off('connected', handleSyncStatus);
      localSync.off('disconnected', handleSyncStatus);
      localSync.off('order_created', handleNewOrder);
    };
  }, [selectedRestaurant, isOffline]);

  // Sync menu to local network when loaded
  useEffect(() => {
    if (menuItems.length > 0 && localSyncStatus.isHost) {
      localSync.syncMenu(menuItems);
    }
  }, [menuItems, localSyncStatus.isHost]);

  // Keyboard shortcuts for POS
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Only handle shortcuts when no modal is open and not typing in input
      if (showPaymentModal || showParkedOrders || showBillSplitter || showDuplicateWarning) return;
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      
      // F1-F8: Select categories (F1 = All, F2 = first category, etc.)
      if (e.key >= 'F1' && e.key <= 'F9') {
        e.preventDefault();
        const fKeyNum = parseInt(e.key.slice(1));
        if (fKeyNum === 1) {
          setSelectedCategory('all');
        } else if (categories[fKeyNum - 2]) {
          setSelectedCategory(categories[fKeyNum - 2]);
        }
        return;
      }
      
      // Escape: Clear order
      if (e.key === 'Escape') {
        clearOrder();
        return;
      }
      
      // Enter or F12: Pay
      if (e.key === 'F12' || (e.key === 'Enter' && e.ctrlKey)) {
        e.preventDefault();
        if (currentOrder.items.length > 0) {
          handlePayClick();
        }
        return;
      }
      
      // F10: Park order
      if (e.key === 'F10') {
        e.preventDefault();
        if (currentOrder.items.length > 0) {
          handleParkOrder();
        }
        return;
      }
      
      // F11: Show parked orders
      if (e.key === 'F11') {
        e.preventDefault();
        setShowParkedOrders(true);
        return;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [categories, currentOrder.items.length, showPaymentModal, showParkedOrders, showBillSplitter, showDuplicateWarning]);

  // Fetch menu items (with offline cache)
  useEffect(() => {
    if (selectedRestaurant) {
      fetchMenu();
      fetchParkedOrders();
    }
  }, [selectedRestaurant]);

  const fetchMenu = async () => {
    try {
      setLoading(true);
      
      // Try to fetch from server
      if (!isOffline) {
        const response = await api.get(`/restaurants/${selectedRestaurant.id}/menu`);
        const items = response.data.filter(item => item.is_available);
        setMenuItems(items);
        
        // Extract unique categories
        const cats = [...new Set(items.map(item => item.category))].filter(Boolean);
        setCategories(cats);
        
        // Cache menu for offline use
        cacheMenu(selectedRestaurant.id, items);
      } else {
        // Load from cache when offline
        const cachedItems = await getOfflineMenu(selectedRestaurant.id);
        if (cachedItems) {
          setMenuItems(cachedItems);
          const cats = [...new Set(cachedItems.map(item => item.category))].filter(Boolean);
          setCategories(cats);
          toast.info('Using cached menu (offline mode)');
        } else {
          toast.error('No cached menu available');
        }
      }
    } catch (error) {
      console.error('Failed to fetch menu:', error);
      // Try to load from cache on error
      const cachedItems = await getOfflineMenu(selectedRestaurant.id);
      if (cachedItems) {
        setMenuItems(cachedItems);
        const cats = [...new Set(cachedItems.map(item => item.category))].filter(Boolean);
        setCategories(cats);
        toast.info('Using cached menu');
      } else {
        toast.error('Failed to load menu');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchParkedOrders = async () => {
    try {
      const response = await api.get(`/restaurants/${selectedRestaurant.id}/parked-orders`);
      setParkedOrders(response.data);
      setParkedOrdersCount(response.data.length);
    } catch (error) {
      console.error('Failed to fetch parked orders:', error);
    }
  };

  // Filter menu items
  const filteredItems = menuItems.filter(item => {
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    const matchesSearch = searchQuery === '' || 
      item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.category?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Order management functions
  const addToOrder = (item) => {
    setCurrentOrder(prev => {
      const existingIndex = prev.items.findIndex(i => i.menu_item_id === item.id);
      
      if (existingIndex >= 0) {
        const newItems = [...prev.items];
        newItems[existingIndex] = {
          ...newItems[existingIndex],
          quantity: newItems[existingIndex].quantity + 1
        };
        return { ...prev, items: newItems };
      }
      
      return {
        ...prev,
        items: [...prev.items, {
          menu_item_id: item.id,
          name: item.name,
          price: item.price,
          quantity: 1,
          notes: '',
          category: item.category
        }]
      };
    });
  };

  const updateItemQuantity = (index, delta) => {
    setCurrentOrder(prev => {
      const newItems = [...prev.items];
      const newQty = newItems[index].quantity + delta;
      
      if (newQty <= 0) {
        newItems.splice(index, 1);
      } else {
        newItems[index] = { ...newItems[index], quantity: newQty };
      }
      
      return { ...prev, items: newItems };
    });
  };

  const removeItem = (index) => {
    setCurrentOrder(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const updateItemNotes = (index, notes) => {
    setCurrentOrder(prev => {
      const newItems = [...prev.items];
      newItems[index] = { ...newItems[index], notes };
      return { ...prev, items: newItems };
    });
  };

  const clearOrder = () => {
    setCurrentOrder({
      items: [],
      notes: '',
      customer_name: '',
      table_number: ''
    });
  };

  // Calculate totals
  const subtotal = currentOrder.items.reduce(
    (sum, item) => sum + (item.price * item.quantity), 
    0
  );

  // Park current order
  const parkOrder = async () => {
    if (currentOrder.items.length === 0) {
      toast.error('No items to park');
      return;
    }

    try {
      const orderData = {
        restaurant_id: selectedRestaurant.id,
        items: currentOrder.items.map(item => ({
          menu_item_id: item.menu_item_id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          notes: item.notes
        })),
        order_type: 'dine_in',
        status: 'parked',
        notes: currentOrder.notes,
        customer_name: currentOrder.customer_name || 'Walk-in',
        table_number: currentOrder.table_number,
        subtotal: subtotal,
        total: subtotal
      };

      await api.post('/orders/park', orderData);
      toast.success('Order parked successfully');
      clearOrder();
      fetchParkedOrders();
    } catch (error) {
      console.error('Failed to park order:', error);
      toast.error('Failed to park order');
    }
  };

  // Resume parked order
  const resumeParkedOrder = async (parkedOrder) => {
    // If current order has items, confirm first
    if (currentOrder.items.length > 0) {
      if (!window.confirm('Current order will be replaced. Continue?')) {
        return;
      }
    }

    try {
      // Set current order from parked
      setCurrentOrder({
        items: parkedOrder.items.map(item => ({
          menu_item_id: item.menu_item_id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          notes: item.notes || '',
          category: item.category
        })),
        notes: parkedOrder.notes || '',
        customer_name: parkedOrder.customer_name || '',
        table_number: parkedOrder.table_number || '',
        parked_order_id: parkedOrder.id
      });

      // Delete from parked
      await api.delete(`/orders/${parkedOrder.id}/parked`);
      setShowParkedOrders(false);
      fetchParkedOrders();
      toast.success('Order resumed');
    } catch (error) {
      console.error('Failed to resume order:', error);
      toast.error('Failed to resume order');
    }
  };

  // Toggle fullscreen
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Handle payment completion
  const handlePaymentComplete = (paymentData) => {
    clearOrder();
    setShowPaymentModal(false);
    fetchParkedOrders();
    toast.success('Payment completed!');
  };

  // Handle offline cash payment
  const handleOfflinePayment = async (paymentMethod) => {
    if (paymentMethod !== 'cash') {
      toast.error('Only cash payments available offline');
      return;
    }

    const orderData = {
      restaurant_id: selectedRestaurant.id,
      items: currentOrder.items.map(item => ({
        menu_item_id: item.menu_item_id,
        name: item.name,
        price: item.price,
        quantity: item.quantity,
        notes: item.notes
      })),
      order_type: 'dine_in',
      status: 'confirmed',
      payment_method: 'cash',
      payment_status: 'paid',
      notes: currentOrder.notes,
      customer_name: currentOrder.customer_name || 'Walk-in',
      table_number: currentOrder.table_number,
      subtotal: subtotal,
      total: subtotal,
      offline_created: true
    };

    try {
      await queueOrder({ orderData });
      
      // Also broadcast to local network if host
      if (localSyncStatus.isHost || localSyncStatus.connected) {
        await localSync.createLocalOrder({
          ...orderData,
          source: 'pos',
          device_id: localStorage.getItem('deviceId') || 'main-pos'
        });
      }
      
      clearOrder();
      toast.success('Order saved offline - will sync when back online', { icon: '📴' });
    } catch (error) {
      console.error('Failed to save offline order:', error);
      toast.error('Failed to save order');
    }
  };

  // Check for duplicate order before showing payment
  const handlePayClick = async () => {
    if (currentOrder.items.length === 0) {
      toast.error('No items in order');
      return;
    }

    // Skip duplicate check when offline
    if (isOffline) {
      setShowPaymentModal(true);
      return;
    }

    try {
      const response = await api.post('/orders/check-duplicate', {
        restaurant_id: selectedRestaurant.id,
        items: currentOrder.items.map(item => ({
          menu_item_id: item.menu_item_id,
          quantity: item.quantity
        })),
        time_window: 30 // 30 minutes
      });

      if (response.data.is_duplicate) {
        setDuplicateOrder(response.data.duplicate_order);
        setShowDuplicateWarning(true);
      } else {
        setShowPaymentModal(true);
      }
    } catch (error) {
      console.error('Failed to check duplicate:', error);
      // Proceed anyway if check fails
      setShowPaymentModal(true);
    }
  };

  // Proceed with payment after duplicate warning
  const handleProceedAfterDuplicate = () => {
    setShowDuplicateWarning(false);
    setDuplicateOrder(null);
    setShowPaymentModal(true);
  };

  // Handle split bill
  const handleSplitBill = async () => {
    if (currentOrder.items.length === 0) {
      toast.error('No items to split');
      return;
    }

    // Create order first, then open splitter
    try {
      const orderData = {
        restaurant_id: selectedRestaurant.id,
        items: currentOrder.items.map(item => ({
          menu_item_id: item.menu_item_id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          notes: item.notes
        })),
        order_type: 'dine_in',
        status: 'confirmed',
        payment_status: 'pending',
        notes: currentOrder.notes,
        customer_name: currentOrder.customer_name || 'Walk-in',
        table_number: currentOrder.table_number,
        subtotal: subtotal,
        total: subtotal
      };

      const response = await api.post('/orders', orderData);
      setSplitOrder({
        id: response.data.order_id || response.data.id,
        items: currentOrder.items,
        total: subtotal,
        customer_name: currentOrder.customer_name,
        table_number: currentOrder.table_number
      });
      setShowBillSplitter(true);
    } catch (error) {
      console.error('Failed to create order for splitting:', error);
      toast.error('Failed to prepare bill for splitting');
    }
  };

  // Handle split complete
  const handleSplitComplete = () => {
    clearOrder();
    setShowBillSplitter(false);
    setSplitOrder(null);
    toast.success('Bill split complete!');
  };

  if (!selectedRestaurant) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">No restaurant selected</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${isFullscreen ? 'fixed inset-0 z-50 bg-slate-100' : ''}`}>
      {/* Offline Banner */}
      <OfflineBanner
        isOffline={isOffline}
        isOnline={isOnline}
        isServerReachable={isServerReachable}
        pendingCount={pendingCount}
        isSyncing={isSyncing}
        lastSyncTime={lastSyncTime}
        onSync={triggerSync}
      />
      
      {/* POS Header */}
      <div className={`bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between ${isOffline || pendingCount > 0 ? 'mt-12' : ''}`}>
        <div className="flex items-center gap-4">
          <h1 className="text-xl font-bold text-slate-900" data-testid="pos-title">
            Point of Sale
          </h1>
          <Badge variant="outline" className="text-orange-600 border-orange-200">
            {selectedRestaurant.name}
          </Badge>
          {/* Offline Indicator in header */}
          <OfflineIndicator 
            isOffline={isOffline}
            pendingCount={pendingCount}
            isSyncing={isSyncing}
            onSync={triggerSync}
          />
          
          {/* Local Sync Status */}
          {(localSyncStatus.isHost || localSyncStatus.connected) && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge 
                    className={`gap-1 ${localSyncStatus.isHost ? 'bg-purple-500' : 'bg-blue-500'}`}
                  >
                    <Server className="w-3 h-3" />
                    {localSyncStatus.isHost ? 'Host' : 'Connected'}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="font-semibold mb-1">Local Network Sync</p>
                  <p className="text-xs">
                    {localSyncStatus.isHost 
                      ? 'This POS is the host. Other devices can connect for offline sync.' 
                      : 'Connected to main POS for local sync.'}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          {/* Parked Orders Button */}
          <Button
            variant="outline"
            onClick={() => setShowParkedOrders(true)}
            className="relative"
            data-testid="parked-orders-btn"
          >
            <ParkingCircle className="w-4 h-4 mr-2" />
            Parked
            {parkedOrdersCount > 0 && (
              <span className="absolute -top-2 -right-2 bg-orange-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                {parkedOrdersCount}
              </span>
            )}
          </Button>

          {/* Keyboard Shortcuts Help */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="text-slate-400">
                  <Keyboard className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="w-48">
                <p className="font-semibold mb-2">Keyboard Shortcuts</p>
                <div className="text-xs space-y-1">
                  <p><kbd className="bg-slate-200 px-1 rounded">F1</kbd> All Items</p>
                  <p><kbd className="bg-slate-200 px-1 rounded">F2-F9</kbd> Categories</p>
                  <p><kbd className="bg-slate-200 px-1 rounded">F10</kbd> Park Order</p>
                  <p><kbd className="bg-slate-200 px-1 rounded">F11</kbd> Parked Orders</p>
                  <p><kbd className="bg-slate-200 px-1 rounded">F12</kbd> Pay</p>
                  <p><kbd className="bg-slate-200 px-1 rounded">Esc</kbd> Clear Order</p>
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {/* View Toggle */}
          <div className="flex border rounded-lg overflow-hidden">
            <Button
              variant={viewMode === 'grid' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('grid')}
              className="rounded-none"
            >
              <Grid3X3 className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
              className="rounded-none"
            >
              <List className="w-4 h-4" />
            </Button>
          </div>

          {/* Fullscreen Toggle */}
          <Button variant="outline" size="icon" onClick={toggleFullscreen}>
            {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {/* Main POS Layout */}
      <div className="flex h-[calc(100vh-180px)]">
        {/* Left Side - Products */}
        <div className="flex-1 flex flex-col bg-slate-50 overflow-hidden">
          {/* Search & Categories */}
          <div className="bg-white border-b border-slate-200 p-4 space-y-3">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="pos-search"
              />
              {searchQuery && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8"
                  onClick={() => setSearchQuery('')}
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>

            {/* Categories */}
            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin">
              <Button
                variant={selectedCategory === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCategory('all')}
                className="whitespace-nowrap"
                data-testid="category-all"
              >
                All Items
              </Button>
              {categories.map(cat => (
                <Button
                  key={cat}
                  variant={selectedCategory === cat ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setSelectedCategory(cat)}
                  className="whitespace-nowrap"
                  data-testid={`category-${cat.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {cat}
                </Button>
              ))}
            </div>
          </div>

          {/* Product Grid */}
          <div className="flex-1 overflow-y-auto p-4">
            <ProductGrid
              items={filteredItems}
              loading={loading}
              viewMode={viewMode}
              onAddItem={addToOrder}
            />
          </div>
        </div>

        {/* Right Side - Order Panel */}
        <div className="w-96 bg-white border-l border-slate-200 flex flex-col">
          <OrderPanel
            order={currentOrder}
            setOrder={setCurrentOrder}
            onUpdateQuantity={updateItemQuantity}
            onRemoveItem={removeItem}
            onUpdateNotes={updateItemNotes}
            onClear={clearOrder}
            onPark={parkOrder}
            onPay={handlePayClick}
            onSplit={handleSplitBill}
            subtotal={subtotal}
          />
        </div>
      </div>

      {/* Payment Modal */}
      <PaymentModal
        open={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        order={currentOrder}
        subtotal={subtotal}
        restaurantId={selectedRestaurant.id}
        onComplete={handlePaymentComplete}
        isOffline={isOffline}
        onOfflinePayment={handleOfflinePayment}
      />

      {/* Duplicate Order Warning */}
      <DuplicateOrderWarning
        open={showDuplicateWarning}
        onClose={() => {
          setShowDuplicateWarning(false);
          setDuplicateOrder(null);
        }}
        duplicateOrder={duplicateOrder}
        onProceed={handleProceedAfterDuplicate}
        onCancel={() => {
          setShowDuplicateWarning(false);
          setDuplicateOrder(null);
        }}
      />

      {/* Bill Splitter */}
      <BillSplitter
        open={showBillSplitter}
        onClose={() => {
          setShowBillSplitter(false);
          setSplitOrder(null);
        }}
        order={splitOrder}
        restaurantId={selectedRestaurant.id}
        onComplete={handleSplitComplete}
      />

      {/* Parked Orders Drawer */}
      <ParkedOrdersDrawer
        open={showParkedOrders}
        onClose={() => setShowParkedOrders(false)}
        orders={parkedOrders}
        onResume={resumeParkedOrder}
        onRefresh={fetchParkedOrders}
      />
    </div>
  );
}
